import { TranslatePipe } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal } from "@angular/core";

import { AnalyticsService } from "../../../services/analytics.service";
import { AnalyticsAction, LineChartInteraction } from "../../../services/analytics.types";
import { CustomDropdown, CustomDropdownOption } from "../../common/custom-dropdown/custom-dropdown";

export interface SubjectGroup<T> {
  name: string;
  subjects: T[];
}

export type SubjectOrGroup<T> = T | SubjectGroup<T>;

export function subjectsToOptions<T extends { name: string }>(
  subjects: SubjectOrGroup<T>[],
): (CustomDropdownOption & { subject?: T })[] {
  const subjectToOption = (s: T) => ({ disabled: false, name: s.name, subject: s });
  const intermediate = subjects.flatMap((s) => {
    if ("subjects" in s)
      return [{ disabled: true, name: s.name }, ...(s as SubjectGroup<T>).subjects.map(subjectToOption)];
    return subjectToOption(s as T);
  });
  return intermediate.map((subject, index) => ({ ...subject, id: index, select: false }));
}

@Component({
  selector: "line-chart-subject-dropdown",
  template: `
    <label class="chart__select-label">{{ label() | translate }}</label>
    <custom-dropdown
      [multiselect]="false"
      [options]="displayOptions()"
      [searchable]="false"
      (selectionChanged)="onSelect($event)"
    />
  `,
  styleUrl: "./line-chart-subject-dropdown.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CustomDropdown, TranslatePipe],
})
export class LineChartSubjectDropdown<T extends { name: string }> {
  readonly subjects = input.required<SubjectOrGroup<T>[]>();
  readonly label = input("Chart.SelectService");
  readonly selected = model<T | undefined>(undefined);

  private readonly analytics = inject(AnalyticsService);
  private readonly _selectedId = signal(0);
  private readonly _options = computed(() => subjectsToOptions(this.subjects()));

  protected readonly displayOptions = computed(() => {
    const id = this._selectedId();
    return this._options().map((o) => ({ ...o, select: !o.disabled && o.id === id }));
  });

  constructor() {
    effect(() => {
      this.subjects();
      this._selectedId.set(0);
    });
    effect(() => this.selected.set(this.displayOptions().find((o) => o.select)?.subject));
  }

  protected onSelect(ids: number[]) {
    const id = ids[0];
    if (id !== undefined) {
      this._selectedId.set(id);
      this.analytics.trackCurrentFeature(AnalyticsAction.InspectLineChart, LineChartInteraction.ServiceChange);
    }
  }
}
